import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { usePanelWidth } from './usePanelWidth.js';

/**
 * The far-right drawer's whole UI-state cluster (audit ARCH-006): which panel is showing, whether the
 * drawer is open, and its draggable/persisted width — plus every way that state changes (toggle, close,
 * open-a-specific-panel, Cmd/Ctrl-Shift-\ cycle) and the two mobile back-gesture effects. Extracted
 * verbatim from App.tsx so open/close/resize/keyboard behavior is byte-for-byte identical.
 *
 * The drawer's Back/Escape close is the browser-history "marker" trick (a same-hash pushState on open,
 * popstate closes it) — NOT `useDismissible`; that wiring is preserved here exactly. The panel-cycle
 * key itself stays in App (its handler also toggles the LEFT sidebar), and calls `cycleRightPanel`.
 *
 * `RIGHT_VIEWS` / `RightViewId` / `SHIPPING_VIEW_INDEX` are exported because both the hook and App's
 * drawer JSX (the header dots, the panel switch) need them.
 */
export type RightViewId = 'assistant' | 'todoist' | 'agenda' | 'shipping' | 'shopping' | 'autonomy';

/**
 * The right-hand drawer's panels, in cycle order. Cmd+] opens the drawer (then cycles through these
 * on each press); the header dots mirror and jump to a panel. Add a panel here (id + title + Material
 * icon) and render its body in the drawer switch below — everything else (cycling, dots) follows.
 */
// The visible drawer slot for purchasing is `shopping` (the Clavero-backed ShoppingDrawer).
export const RIGHT_VIEWS: ReadonlyArray<{ id: RightViewId; title: string; icon: string }> = [
  // The ✦ assistant lives in the global header (IconBar popover), not the drawer.
  { id: 'todoist', title: 'Today', icon: 'task_alt' },
  { id: 'agenda', title: 'Agenda', icon: 'calendar_month' },
  { id: 'shipping', title: 'Shipments', icon: 'local_shipping' },
  { id: 'shopping', title: 'Shopping', icon: 'shopping_bag' },
  // AXIS X18 — the passive autonomy review surface (recap / scorecard / rules ledger).
  { id: 'autonomy', title: 'Autonomy', icon: 'auto_awesome' },
];
/** Drawer index of the Shipments panel — the assistant jumps here to show a shipping draft to buy. */
export const SHIPPING_VIEW_INDEX = RIGHT_VIEWS.findIndex((v) => v.id === 'shipping');

export interface UseRightDrawer {
  /** Whether the drawer is open. */
  rightOpen: boolean;
  /** Index into RIGHT_VIEWS of the visible panel. */
  rightViewIndex: number;
  /** Draggable, persisted drawer width (px). */
  rightWidth: number;
  /** Handle onMouseDown → begin a width drag. */
  startRightResize: (e: ReactMouseEvent) => void;
  /** Handle onDoubleClick → reset to the default width. */
  resetRightWidth: () => void;
  /** Open the drawer on a specific panel (by RightViewId or index). No-op for an unknown id. */
  openRightPanel: (idOrIndex: RightViewId | number) => void;
  /** Jump to a panel without changing open state (the header dots). */
  setRightViewIndex: (index: number) => void;
  /** Close the drawer. */
  closeRight: () => void;
  /** Toggle the drawer open/closed. */
  toggleRight: () => void;
  /** Cmd/Ctrl-Shift-\ rotate: closed → panel 0 → … → last → closed. */
  cycleRightPanel: () => void;
  /** True when the drawer is open and `id` is the visible panel. */
  panelVisible: (id: RightViewId) => boolean;
  /** Live-value ref of `rightOpen`, for other global key/popstate handlers that must not re-bind. */
  rightOpenRef: React.MutableRefObject<boolean>;
}

export function useRightDrawer(): UseRightDrawer {
  // Right-hand drawer (the far-right panel icon): open state + which panel of RIGHT_VIEWS is showing.
  // Cmd/Ctrl-Shift-\ opens then cycles the panel, wrapping past the last back to closed. A ref lets the
  // global key handler read the live open state without re-binding the listener.
  const [rightOpen, setRightOpen] = useState(false);
  const [rightViewIndex, setRightViewIndex] = useState(0);
  const rightOpenRef = useRef(rightOpen);
  rightOpenRef.current = rightOpen;

  // Draggable, persisted panel width. The right drawer is docked right, so its handle is on the LEFT
  // edge → `invert` (drag left widens it).
  const [rightWidth, startRightResize, resetRightWidth] = usePanelWidth({
    storageKey: 'lapo-right-width',
    min: 280,
    max: 560,
    initial: 320,
    invert: true,
  });

  // Mobile: the right drawer should close on the browser Back button/gesture (natural on a phone). We
  // push a marker history entry (same hash, so the hash listener above ignores it) when it opens; Back
  // pops that entry and closes the drawer. Closing via the scrim/toggle instead pops the marker itself.
  const rightPushedRef = useRef(false);
  useEffect(() => {
    const isNarrowViewport = window.matchMedia('(max-width: 768px)').matches;
    if (rightOpen && isNarrowViewport && !rightPushedRef.current) {
      rightPushedRef.current = true;
      window.history.pushState({ lapoDrawer: 'right' }, '', window.location.hash);
    } else if (!rightOpen && rightPushedRef.current) {
      rightPushedRef.current = false;
      if ((window.history.state as { lapoDrawer?: string } | null)?.lapoDrawer === 'right') window.history.back();
    }
  }, [rightOpen]);
  useEffect(() => {
    const onPop = (): void => {
      if (rightOpenRef.current) {
        rightPushedRef.current = false; // Back consumed our marker entry
        setRightOpen(false);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const closeRight = useCallback(() => setRightOpen(false), []);
  const toggleRight = useCallback(() => setRightOpen((o) => !o), []);

  const openRightPanel = useCallback((idOrIndex: RightViewId | number) => {
    const idx = typeof idOrIndex === 'number' ? idOrIndex : RIGHT_VIEWS.findIndex((v) => v.id === idOrIndex);
    if (idx >= 0) {
      setRightViewIndex(idx);
      setRightOpen(true);
    }
  }, []);

  // Cmd/Ctrl-Shift-\ rotate: closed → panel 0 → … → last panel → closed.
  const cycleRightPanel = useCallback(() => {
    if (!rightOpenRef.current) {
      setRightViewIndex(0);
      setRightOpen(true);
    } else {
      setRightViewIndex((i) => {
        const next = i + 1;
        if (next >= RIGHT_VIEWS.length) {
          setRightOpen(false); // rotated past the end → hide the drawer
          return 0; // reset so the next open starts at the first panel
        }
        return next;
      });
    }
  }, []);

  const panelVisible = useCallback(
    (id: RightViewId) => rightOpen && RIGHT_VIEWS[rightViewIndex]?.id === id,
    [rightOpen, rightViewIndex],
  );

  return {
    rightOpen,
    rightViewIndex,
    rightWidth,
    startRightResize,
    resetRightWidth,
    openRightPanel,
    setRightViewIndex,
    closeRight,
    toggleRight,
    cycleRightPanel,
    panelVisible,
    rightOpenRef,
  };
}
